import { type AiGeneratedCover } from 'components/PhotoBook/PhotoBookProvider/PhotoBook.types'
import {
  AI_COVER_TEMPLATE_SLUG,
  RIVIERA_COVER_TEMPLATE_SLUG,
  SKETCH_COVER_TEMPLATE_SLUG,
} from 'pages/PhotoBookPage/components/BookControls/components/CoverEdit/aiCover.consts'
import iconicThumbnailUrl from './assets/style-iconic.webp'
import rivieraThumbnailUrl from './assets/style-riviera.webp'
import sketchThumbnailUrl from './assets/style-sketch.webp'

export const AI_COVER_TITLE_MAX_LENGTH = 30
// Below this the counter stays hidden, so an empty field doesn't show "0/30" over the placeholder.
export const AI_COVER_TITLE_COUNTER_MIN_LENGTH = 25

type CoverStyle = {
  id: string
  nameKey: string
  thumbnailUrl: string
  analyticsName: string
  generation: { templateSlug: string; isPhotoRequired: boolean }
  // Matches the backend's isPrintUpscaledAiCoverStyle: the image's outer band prints on the wrap.
  isPrintedWithWrapBand: boolean
  // Matches the backend's getAiCoverSpineAndBackColors.
  backCover: 'sampled-colors' | 'photo-cover' | 'paper-color'
  keepsDefaultBoardInsides: boolean
}

// The ids are the backend's AI_COVER_STYLES.
export const COVER_STYLES = [
  {
    id: 'iconic',
    nameKey: 'photo_book.cover_edit.style_iconic',
    thumbnailUrl: iconicThumbnailUrl,
    analyticsName: 'Iconic',
    generation: {
      templateSlug: AI_COVER_TEMPLATE_SLUG,
      isPhotoRequired: false,
    },
    isPrintedWithWrapBand: false,
    backCover: 'sampled-colors',
    keepsDefaultBoardInsides: false,
  },
  {
    id: 'riviera',
    nameKey: 'photo_book.cover_edit.style_riviera',
    thumbnailUrl: rivieraThumbnailUrl,
    analyticsName: 'Riviera',
    generation: {
      templateSlug: RIVIERA_COVER_TEMPLATE_SLUG,
      isPhotoRequired: true,
    },
    isPrintedWithWrapBand: true,
    backCover: 'photo-cover',
    keepsDefaultBoardInsides: true,
  },
  {
    id: 'sketch',
    nameKey: 'photo_book.cover_edit.style_sketch',
    thumbnailUrl: sketchThumbnailUrl,
    analyticsName: 'Sketch',
    generation: {
      templateSlug: SKETCH_COVER_TEMPLATE_SLUG,
      isPhotoRequired: true,
    },
    isPrintedWithWrapBand: true,
    backCover: 'paper-color',
    keepsDefaultBoardInsides: true,
  },
] as const satisfies readonly CoverStyle[]

type CoverStyleEntry = (typeof COVER_STYLES)[number]

export type CoverStyleId = CoverStyleEntry['id']

export type PhotoCoverStyleId = Extract<
  CoverStyleEntry,
  { generation: { isPhotoRequired: true } }
>['id']

export function getCoverStyle<Id extends CoverStyleId>(
  styleId: Id
): Extract<CoverStyleEntry, { id: Id }> {
  const style = COVER_STYLES.find(
    (entry): entry is Extract<CoverStyleEntry, { id: Id }> =>
      entry.id === styleId
  )
  if (!style) throw new Error(`Unknown cover style: ${styleId}`)
  return style
}

export function isCoverStyleId(value: unknown): value is CoverStyleId {
  return COVER_STYLES.some(({ id }) => id === value)
}

export function isPhotoCoverStyle(
  styleId: CoverStyleId
): styleId is PhotoCoverStyleId {
  return getCoverStyle(styleId).generation.isPhotoRequired
}

// A cover saved before covers carried their style is an Iconic one.
export function getAiCoverStyle(aiCover: AiGeneratedCover) {
  return getCoverStyle(aiCover.style ?? 'iconic')
}

export function getAiCoverStyleAnalyticsName(aiCover: AiGeneratedCover) {
  return getAiCoverStyle(aiCover).analyticsName
}
